import { Col, Row } from 'react-bootstrap';
import { useFormContext, useWatch } from 'react-hook-form';

import AirportDropdown from '@/components/AirportDropdown';
import CompanyDropdown from '@/components/CompanyDropdown';
import DateInput from '@/common/DateInput';
import Field from '@/common/Field';
import Form from '@/common/Form';
import HasPermission from '@/components/HasPermission';
import PhoneInput from '@/common/PhoneInput';
import SelectActiveStatus from '@/components/SelectActiveStatus';
import SelectAirportGroupPermission from '@/components/SelectAirportGroupPermission';
import SelectGroup from '@/components/SelectGroup';
import SelectRole from '@/components/SelectRole';
import SelectState from '@/components/SelectState';
import Textarea from '@/common/Textarea';
import { UserModalTypes } from '@/pages/Users/components/UserModal/types';
import { useEffect } from 'react';
import useSession from '@/state/session';

const UserProfileForm = ({ takenUsername, onUsernameBlur }: UserModalTypes.ProfileFormProps) => {
  const disabled = !HasPermission.check('allowUpdateUserProfile');
  const driverRoleId = useSession(({ state }) => state.config.driverRoleId);

  // Primary Airport is only required for drivers - Zod enforces that, but react-hook-form only
  // revalidates the field that actually changed, so switching roles doesn't recheck it on its own.
  const { trigger } = useFormContext();
  const roleId = useWatch({ name: 'roleId' });
  useEffect(() => {
    trigger('primaryAirportCode');
  }, [roleId, trigger]);

  // The taken-username result arrives after the blur lookup, so recheck the field once it lands.
  useEffect(() => {
    trigger('username');
  }, [takenUsername, trigger]);

  return (
    <>
      <Form.Input name="id" type="hidden" />
      <Row>
        <Col>
          <Form.Field name="roleId" label="Type">
            <Form.Input name="roleId" as={SelectRole} placeholder="--" searchable validate disabled={disabled} />
          </Form.Field>
        </Col>
        <Col>
          <Field label="Employee ID">
            <Form.Input name="employeeId" disabled={disabled} />
          </Field>
        </Col>
      </Row>
      <Row>
        <Col>
          <Form.Field name="username" label="Username">
            <Form.Input name="username" validate disabled={disabled} onBlur={(event) => onUsernameBlur?.(event.target.value)} />
          </Form.Field>
        </Col>
        <Col>
          <Form.Field name="email" label="Email">
            <Form.Input name="email" type="email" validate disabled={disabled} />
          </Form.Field>
        </Col>
      </Row>
      <Form.Watch name="id">
        {([id]) =>
          !id && (
            <Row>
              <Col md={6}>
                <Form.Field name="password" label="Password">
                  <Form.Input name="password" type="password" placeholder="Password" validate disabled={disabled} />
                </Form.Field>
                <Form.Field name="confirmPassword" label="Confirm Password">
                  <Form.Input name="confirmPassword" type="password" placeholder="Password" validate disabled={disabled} />
                </Form.Field>
              </Col>
            </Row>
          )
        }
      </Form.Watch>
      <Row>
        <Col>
          <Form.Field name="firstName" label="First Name">
            <Form.Input name="firstName" validate disabled={disabled} />
          </Form.Field>
        </Col>
        <Col>
          <Form.Field name="phone" label="Phone">
            <Form.Input name="phone" as={PhoneInput} placeholder="(___) ___-____" validate disabled={disabled} />
          </Form.Field>
        </Col>
      </Row>
      <Row>
        <Col>
          <Form.Field name="lastName" label="Last Name">
            <Form.Input name="lastName" validate disabled={disabled} />
          </Form.Field>
        </Col>
        <Col>
          <Form.Field name="airportCodes" label="Assigned Airports">
            <Form.Watch name="airportCodes">
              {([airportCodes], form) => (
                <SelectAirportGroupPermission
                  value={(airportCodes || []).map((node) => node?.permissionId || node)}
                  onChange={(event) =>
                    form.setValue(
                      'airportCodes',
                      (event.target.value || []).map((permissionId) => ({ permissionId })),
                      { shouldDirty: true }
                    )
                  }
                  placeholder="--"
                  multiple
                  searchable
                  disabled={disabled}
                />
              )}
            </Form.Watch>
          </Form.Field>
        </Col>
      </Row>
      <Row>
        <Col>
          <Form.Field name="companyId" label="Company">
            <Form.Input name="companyId" as={CompanyDropdown} options={{ locale: { 'Select...': '--' } }} validate disabled={disabled} />
          </Form.Field>
        </Col>
        <Col>
          <Form.Field name="primaryAirportCode" label="Primary Airport">
            <Form.Input
              name="primaryAirportCode"
              as={AirportDropdown}
              options={{ locale: { 'Select...': 'Airport' } }}
              validate={roleId === driverRoleId}
              disabled={disabled}
            />
          </Form.Field>
        </Col>
      </Row>
      <Row>
        <Col>
          <Field label="Full Address">
            <Form.Input name="address" disabled={disabled} />
          </Field>
        </Col>
        <Col>
          <Form.Watch name="groups">
            {([groups = []], form) => (
              <Field label="Assigned Roles">
                <SelectGroup
                  value={groups?.map((group) => group.id)}
                  onChange={(event) => {
                    const val = event?.target?.value ?? event;
                    const exists = !!groups.find((group) => group.id === val);
                    form.setValue('groups', exists ? groups.filter((group) => group.id !== val) : [...groups, { id: val }], {
                      shouldDirty: true,
                    });
                  }}
                  placeholder="--"
                  searchable
                  disabled={disabled}
                />
              </Field>
            )}
          </Form.Watch>
        </Col>
      </Row>
      <Row>
        <Col>
          <Field label="City">
            <Form.Input name="city" disabled={disabled} />
          </Field>
        </Col>
        <Col>
          <Form.Field name="dob" label="Birth Date">
            <Form.Input name="dob" as={DateInput} disabled={disabled} />
          </Form.Field>
        </Col>
      </Row>
      <Row>
        <Col>
          <Form.Field name="state" label="State">
            <Form.Input name="state" as={SelectState} placeholder="--" searchable disabled={disabled} />
          </Form.Field>
        </Col>
        <Col>
          <Form.Watch name={['id', 'startDate']}>
            {([id, startDate]) => {
              // A user who can see the training checklist needs a hire date before the checklist is usable.
              const hireDateRequired = !!id && !startDate && HasPermission.check('allowViewTrainingChecklist');
              return (
                <Form.Field
                  name="startDate"
                  label="Hire Date"
                  className={hireDateRequired ? 'ProfileForm-HireDateWarning' : undefined}
                  feedback={hireDateRequired ? 'Warning: Hire date required to use training checklist' : undefined}
                >
                  <Form.Input name="startDate" as={DateInput} disabled={disabled} />
                </Form.Field>
              );
            }}
          </Form.Watch>
        </Col>
      </Row>
      <Row>
        <Col>
          <Form.Field name="zipCode" label="Zip">
            <Form.Input name="zipCode" disabled={disabled} />
          </Form.Field>
        </Col>
        <Col>
          <Form.Field name="active" label="Status">
            <Form.Input name="active" as={SelectActiveStatus} placeholder="--" validate disabled={disabled} />
          </Form.Field>
        </Col>
      </Row>
      <Row>
        <Col>
          <Form.Field name="notes" label="Medical Info">
            <Form.Input name="notes" as={Textarea} disabled={disabled} />
          </Form.Field>
        </Col>
        <Col>
          <div className="ProfileForm-LongTrip">
            <span>Long Trips?</span>
            <span>
              <Form.Switch name="longTrip" disabled={disabled} />
            </span>
          </div>
        </Col>
      </Row>
    </>
  );
};

export default UserProfileForm;
